import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    HStack,
    Portal,
    Text,
    VStack,
} from "@chakra-ui/react";
import { FormTextarea } from "@/components/jobOffer/ui";
import CandidateStatusBadge from "../CandidateStatusBadge";

// Final decisions need a justification (enforced by the API too).
const FINAL_DECISIONS = ["QualifiedForNextStage", "ReadyToHire", "Hired", "Rejected"];

const CANDIDATE_MANUAL_STATUSES = [
    "Initialized", "Created", "QualifiedForNextStage",
    "FurtherSelection", "ReadyToHire", "Hired", "Rejected", "CandidateResigned",
];

const ChangeStatusModal = ({ isOpen, onClose, currentStatus, onSubmit, isLoading }) => {
    const { t } = useTranslation("models");
    const [selectedStatus, setSelectedStatus] = useState(currentStatus);
    const [note, setNote] = useState("");
    const needsReason = FINAL_DECISIONS.includes(selectedStatus);

    useEffect(() => {
        if (isOpen) {
            setSelectedStatus(currentStatus);
            setNote("");
        }
    }, [isOpen, currentStatus]);

    const handleSubmit = () => {
        onSubmit(selectedStatus, note.trim() || null);
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => { if (!e.open) onClose(); }}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content
                        bg="white"
                        color="gray.900"
                        borderColor="gray.200"
                        border="1px solid"
                        boxShadow="0 4px 24px rgba(23,21,31,0.12)"
                        w={{ base: "calc(100vw - 2rem)", md: "lg" }}
                        maxW="100%"
                        maxH="calc(100dvh - 2rem)"
                        overflowY="auto"
                        borderRadius={12}
                    >
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight={700} fontSize="lg" mb={4} color="gray.900">
                                {t("candidateDetailPage.changeStatusModalTitle")}
                            </Text>
                            <VStack align="stretch" gap={2} mb={4}>
                                {CANDIDATE_MANUAL_STATUSES.map(s => (
                                    <Box
                                        key={s}
                                        as="button"
                                        type="button"
                                        onClick={() => setSelectedStatus(s)}
                                        display="flex"
                                        alignItems="center"
                                        gap={3}
                                        px={3}
                                        py={2}
                                        borderRadius={8}
                                        border="1px solid"
                                        borderColor={selectedStatus === s ? "purple.400" : "gray.200"}
                                        bg={selectedStatus === s ? "purple.50" : "gray.50"}
                                        cursor="pointer"
                                        transition="all 0.15s"
                                        _hover={{ borderColor: "purple.400" }}
                                        outline="none"
                                        textAlign="left"
                                    >
                                        <CandidateStatusBadge status={s} />
                                    </Box>
                                ))}
                            </VStack>
                            <Text fontSize="sm" color="gray.500" mb={2}>
                                {t(needsReason ? "candidateDetailPage.changeStatusModalNoteRequired" : "candidateDetailPage.changeStatusModalNote")}
                            </Text>
                            <FormTextarea
                                placeholder={t("candidateDetailPage.changeStatusModalNotePlaceholder")}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={3}
                                resize="vertical"
                                mb={4}
                            />
                            <HStack justify="flex-end" gap={2}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    color="gray.600"
                                    _hover={{ bg: "gray.100" }}
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t("candidateDetailPage.changeStatusModalCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    loading={isLoading}
                                    disabled={!selectedStatus || (needsReason && !note.trim())}
                                    onClick={handleSubmit}
                                >
                                    {t("candidateDetailPage.changeStatusModalConfirm")}
                                </Button>
                            </HStack>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" color="gray.400" _hover={{ bg: "gray.100", color: "gray.600" }} />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default ChangeStatusModal;
